import React, { useEffect, useState } from "react";
import { Order } from "../../../api/order";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import {
  DSTable,
  DSTableBody,
  DSTableCell,
  DSTableHead,
  DSTableRow,
} from "../../system-design/Table";
import collectionApiClient, { Collection } from "../../../api/collection";
import DSAvatar from "../../system-design/DSAvatar";
import DSButton from "../../system-design/DSButton";
import { sectionCardSx } from "../../system-design/sectionCardSx";

interface OrderCollectionsSectionProps {
  collections: Order["collections"];
  onCollectionClick: (collectionId: string) => void;
}

const OrderCollectionsSection = ({
  collections,
  onCollectionClick,
}: OrderCollectionsSectionProps) => {
  const [collectionsData, setCollectionsData] = useState<
    Map<string, Collection>
  >(new Map());
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (collections && collections.length > 0) {
      setHasLoaded(false);
      Promise.all(
        collections.map((col) =>
          collectionApiClient
            .getCollectionById(col.collectionId)
            .then((data) => ({ id: col.collectionId, data }))
            .catch((error) => {
              console.error(
                `Failed to load collection ${col.collectionId}:`,
                error,
              );
              return { id: col.collectionId, data: null };
            }),
        ),
      ).then((results) => {
        const collectionsMap = new Map<string, Collection>();
        results.forEach(({ id, data }) => {
          if (data) {
            collectionsMap.set(id, data);
          }
        });
        setCollectionsData(collectionsMap);
        setHasLoaded(true);
      });
    }
  }, [collections]);

  if (!collections || collections.length === 0) {
    return null;
  }

  return (
    <DSBox sx={[{ mt: 3, p: 2 }, sectionCardSx]}>
      <DSTypography variant="h6" gutterBottom>
        Collections ({collections.length})
      </DSTypography>
      <DSTable>
        <DSTableHead>
          <DSTableRow>
            <DSTableCell>Cover</DSTableCell>
            <DSTableCell>Collection Name</DSTableCell>
            <DSTableCell>Collection ID</DSTableCell>
            <DSTableCell>Code</DSTableCell>
            <DSTableCell>Processed At</DSTableCell>
          </DSTableRow>
        </DSTableHead>
        <DSTableBody>
          {collections.map((collection, index) => {
            const collectionDetails = collectionsData.get(
              collection.collectionId,
            );
            return (
              <DSTableRow key={index}>
                <DSTableCell>
                  <DSAvatar
                    src={collectionDetails?.coverUrl}
                    alt={collectionDetails?.name || "Collection"}
                    sx={{ width: 50, height: 50 }}
                  />
                </DSTableCell>
                <DSTableCell>
                  {collectionDetails?.name ||
                    (hasLoaded ? "Not found" : "Loading...")}
                </DSTableCell>
                <DSTableCell>
                  <DSButton
                    size="small"
                    variant="text"
                    onClick={() => onCollectionClick(collection.collectionId)}
                  >
                    {collection.collectionId}
                  </DSButton>
                </DSTableCell>
                <DSTableCell>
                  {collection.code ? <code>{collection.code}</code> : "-"}
                </DSTableCell>
                <DSTableCell>
                  {collection.processedAt
                    ? new Date(collection.processedAt).toLocaleString()
                    : "-"}
                </DSTableCell>
              </DSTableRow>
            );
          })}
        </DSTableBody>
      </DSTable>
    </DSBox>
  );
};

export default OrderCollectionsSection;
